// week-calendar.component.ts
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { runtimeLogger } from '../../../utils/runtime-logger.util';
import { Router } from '@angular/router'; // Adicionado
import { AppointmentsService } from '../../../services/appointments.service';
import {
  AgendamentoSemanaAPI,
  AppointmentSemanaResponse
} from '../../../interfaces/appointment.interface';
import { LocalStorageUtil } from '../../../utils/local-storage.util';

interface DayColumn {
  date: Date;
  dayName: string;
  dayNumber: string;
  monthName: string;
  isToday: boolean;
  dateISO: string;
}

interface TimeSlot {
  hour: string;
  formattedHour: string;
}

interface CalendarAppointment {
  appointment: AgendamentoSemanaAPI;
  dayIndex: number;
  timeSlotIndex: number;
  startPosition: number;
}

@Component({
  selector: 'app-week-calendar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './week-calendar.html',
  styleUrls: ['./week-calendar.scss']
})
export class WeekCalendar implements OnInit {
  currentDate: Date = new Date();

  weekDays: DayColumn[] = [];
  timeSlots: TimeSlot[] = [];

  totalAgendamentos: number = 0;
  nomeMesAno: string = '';
  currentMonthYear: string = '';
  appointments: CalendarAppointment[] = [];

  loading: boolean = false;
  error: boolean = false;

  // Variáveis para o pop-up
  selectedAppointment: AgendamentoSemanaAPI | null = null;
  popupPosition = { x: 0, y: 0 };
  showPopup = false;

  constructor(
    private appointmentsService: AppointmentsService,
    private router: Router // Adicionado
  ) {}

  ngOnInit(): void {
    this.generateTimeSlots();
    this.loadAppointmentsForWeek();
  }

  private generateTimeSlots(): void {
    this.timeSlots = [];

    for (let hour = 8; hour <= 19; hour++) {
      const hourFormatted = hour.toString().padStart(2, '0');

      this.timeSlots.push({
        hour: `${hourFormatted}:00`,
        formattedHour: this.formatHourToAMPM(`${hourFormatted}:00`)
      });

      this.timeSlots.push({
        hour: `${hourFormatted}:30`,
        formattedHour: ''
      });
    }
  }

  private generateWeekDays(): void {
    this.weekDays = [];

    const startOfWeek = new Date(this.currentDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day;
    startOfWeek.setDate(diff);

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);

      const today = new Date();
      const isToday = date.getDate() === today.getDate() &&
                     date.getMonth() === today.getMonth() &&
                     date.getFullYear() === today.getFullYear();

      this.weekDays.push({
        date,
        dayName: date.toLocaleDateString('pt-BR', { weekday: 'short' }),
        dayNumber: date.getDate().toString(),
        monthName: date.toLocaleDateString('pt-BR', { month: 'short' }),
        isToday,
        dateISO: this.formatDateToISO(date)
      });
    }

    this.updateMonthYearTitle();
  }

  private updateMonthYearTitle(): void {
    if (this.weekDays.length > 0) {
      const middleDay = this.weekDays[3];
      this.currentMonthYear = middleDay.date.toLocaleDateString('pt-BR', {
        month: 'long',
        year: 'numeric'
      }).replace(/^\w/, c => c.toUpperCase());
    } else {
      this.currentMonthYear = this.currentDate.toLocaleDateString('pt-BR', {
        month: 'long',
        year: 'numeric'
      }).replace(/^\w/, c => c.toUpperCase());
    }
  }

  loadAppointmentsForWeek(): void {
    this.loading = true;
    this.error = false;

    this.generateWeekDays();

    const dataISO = this.formatDateToISO(this.currentDate);

    this.appointmentsService.getAppointmentsSemana(LocalStorageUtil.getSelectedVWDn(), dataISO).subscribe({
      next: (response: AppointmentSemanaResponse) => {
        this.totalAgendamentos = response.totalAgendamentos;
        this.nomeMesAno = response.mesAno;
        this.processAppointments(response.agendamentos);
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar agendamentos da semana:', error);
        this.error = true;
        this.loading = false;
      }
    });
  }

  private formatDateToISO(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private processAppointments(agendamentosAPI: AgendamentoSemanaAPI[]): void {
    this.appointments = [];

    agendamentosAPI.forEach(apiAppointment => {
      try {
        const dayIndex = this.getDayIndexFromDateStr(apiAppointment.dataAgendamento);

        if (dayIndex === -1) {
          return;
        }

        const timeSlotIndex = this.getTimeSlotIndex(apiAppointment.horarioAgendamento);

        if (timeSlotIndex === -1) {
          return;
        }

        const startPosition = this.calculatePositionInCell(apiAppointment.horarioAgendamento);

        this.appointments.push({
          appointment: apiAppointment,
          dayIndex,
          timeSlotIndex,
          startPosition
        });

      } catch (error) {
        console.error('Erro ao processar agendamento:', error, apiAppointment);
      }
    });
  }

  private getDayIndexFromDateStr(dateStr: string): number {
    try {
      const [day, month, year] = dateStr.split('/').map(Number);
      const appointmentDate = new Date(year, month - 1, day);

      const appointmentDateISO = this.formatDateToISO(appointmentDate);

      return this.weekDays.findIndex(day => day.dateISO === appointmentDateISO);
    } catch (error) {
      console.error('Erro ao converter data:', error, dateStr);
      return -1;
    }
  }

  private getTimeSlotIndex(timeStr: string): number {
    try {
      const [hourStr, minuteStr] = timeStr.split(':');
      const hour = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);

      if (isNaN(hour) || isNaN(minute)) {
        return -1;
      }

      const totalMinutes = (hour * 60) + minute;

      if (totalMinutes < 480 || totalMinutes > 1170) {
        return -1;
      }

      const slotIndex = Math.floor((totalMinutes - 480) / 30);

      return slotIndex;

    } catch (error) {
      console.error('Erro em getTimeSlotIndex:', error, timeStr);
      return -1;
    }
  }

  private calculatePositionInCell(timeStr: string): number {
    try {
      const [hourStr, minuteStr] = timeStr.split(':');
      const hour = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);

      if (isNaN(hour) || isNaN(minute)) {
        return 0;
      }

      const totalMinutes = (hour * 60) + minute;

      const slotStartMinutes = Math.floor((totalMinutes - 480) / 30) * 30 + 480;

      const minutesInSlot = totalMinutes - slotStartMinutes;

      const position = minutesInSlot;

      return Math.max(0, Math.min(position, 29));

    } catch (error) {
      console.error('Erro ao calcular posição:', error, timeStr);
      return 0;
    }
  }

  formatHourToAMPM(time: string): string {
    try {
      const [hourStr] = time.split(':');
      const hour = parseInt(hourStr, 10);

      if (isNaN(hour)) {
        return time;
      }

      if (hour === 0) {
        return '12AM';
      } else if (hour === 12) {
        return '12PM';
      } else if (hour > 12) {
        return `${hour - 12}PM`;
      } else {
        return `${hour}AM`;
      }
    } catch (error) {
      console.error('Erro ao formatar hora:', error, time);
      return time;
    }
  }

  formatDateToDisplay(dateStr: string): string {
    try {
      const [day, month, year] = dateStr.split('/').map(Number);
      const monthNames = [
        'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
        'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
      ];

      return `${day} de ${monthNames[month - 1]} de ${year}`;
    } catch (error) {
      return dateStr;
    }
  }

  getAppointmentsForCell(dayIndex: number, timeSlotIndex: number): CalendarAppointment[] {
    return this.appointments.filter(apt => {
      const aptTimeSlot = this.getTimeSlotIndex(apt.appointment.horarioAgendamento);
      return apt.dayIndex === dayIndex && aptTimeSlot === timeSlotIndex;
    });
  }

  onAppointmentClick(event: MouseEvent, appointment: AgendamentoSemanaAPI): void {
    event.stopPropagation();

    this.selectedAppointment = appointment;
    this.popupPosition = {
      x: event.clientX + 10,
      y: event.clientY + 10
    };
    this.showPopup = true;
  }

  // ===== NOVO MÉTODO ADICIONADO =====
  // Método para navegar para detalhes do lead
  navigateToLeadDetails(leadId: number): void {
    this.closePopup(); // Fecha o popup se estiver aberto

    if (leadId) {
      this.router.navigate(['/leads', leadId, 'detalhes']);
    } else {
      runtimeLogger.warn('LeadId não encontrado');
      // Opcional: mostrar uma mensagem para o usuário
      alert('Não foi possível encontrar o ID do lead');
    }
  }

  closePopup(): void {
    this.showPopup = false;
    this.selectedAppointment = null;
  }

  goToCurrentWeek(): void {
    this.currentDate = new Date();
    this.loadAppointmentsForWeek();
  }

  previousWeek(): void {
    const newDate = new Date(this.currentDate);
    newDate.setDate(newDate.getDate() - 7);
    this.currentDate = newDate;
    this.loadAppointmentsForWeek();
  }

  nextWeek(): void {
    const newDate = new Date(this.currentDate);
    newDate.setDate(newDate.getDate() + 7);
    this.currentDate = newDate;
    this.loadAppointmentsForWeek();
  }
}
